import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { chat } from 'src/backend';
import useAuth from './useAuth';
import {
  getConversationType,
  getUnreadMessagesCount,
} from 'src/utils/conversations';
import { PATH_DASHBOARD } from 'src/routes/paths';
import FirestoreAdapter from 'src/adapters/FirestoreAdapter';
import SocketIO from 'src/backend/SocketIO';

const ChatClientContext = createContext();

const TOKEN_RETRY_DELAYS_MS = [2000, 5000, 15000];

// fetch() rejects with a bare TypeError when the request never reached the
// server (offline, flaky mobile network, Safari's "Load failed").
function isNetworkError(err) {
  return (
    err instanceof TypeError &&
    /failed to fetch|load failed|networkerror|network connection was lost/i.test(
      err.message
    )
  );
}

export function useChatToken() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [firebaseToken, setFirebaseToken] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  // Only businesses/npos can chat (for now)
  const canChat =
    user &&
    user.account_type === 'TEAM-STAFF' &&
    !(user.is_executive || user.is_corporate) &&
    user.team_id && // Must have a team_id to chat
    !user.is_admin_for_executive && // Exclude admin for executive
    !user.is_admin_for_corporate; // Exclude admin for corporate

  useEffect(() => {
    if (valid || !canChat) {
      return;
    }

    setLoading(true);

    if (!user) {
      setLoading(false);
      setValid(true);
      setFirebaseToken(null);
      return;
    }

    let cancelled = false;
    let retryTimer;
    const finish = () => {
      setValid(true);
      setLoading(false);
    };
    const load = (attempt) => {
      chat
        .getFirebaseToken()
        .then((fbToken) => {
          if (cancelled) return;
          setFirebaseToken(fbToken);
          finish();
        })
        .catch((err) => {
          if (cancelled) return;
          if (isNetworkError(err)) {
            if (attempt < TOKEN_RETRY_DELAYS_MS.length) {
              retryTimer = setTimeout(
                () => load(attempt + 1),
                TOKEN_RETRY_DELAYS_MS[attempt]
              );
              return;
            }
            // Still offline: leave chat unavailable rather than report a
            // connectivity problem the user can't act on.
          } else {
            Sentry.captureException(err);
            if (err.message !== 'Authenticate') {
              enqueueSnackbar(err.message ?? 'Failed to start a chat session', {
                variant: 'error',
              });
            }
          }
          finish();
        });
    };
    load(0);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [valid, user, enqueueSnackbar, canChat]);

  const invalidate = useCallback(() => setValid(false), []);

  // Only invalidate when relevant user properties change, not the entire user object
  const userId = user?.id;
  const userTeamId = user?.team_id;
  const userAccountType = user?.account_type;
  useEffect(() => {
    invalidate();
  }, [userId, userTeamId, userAccountType, invalidate]);

  return {
    firebaseToken,
    isLoading,
    invalidate,
  };
}

export function ChatClientProvider({ children }) {
  const { user, invalidateUserData } = useAuth();
  const { firebaseToken } = useChatToken();
  const [chatOpen, setChatOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [conversations, setConversations] = useState({});
  const [conversationParticipants, setConversationParticipants] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  // pendingChat: { type: 'donation'|'team'|'admin', id?, title? } or null
  const [pendingChat, setPendingChat] = useState(null);
  const [ready, setReady] = useState(false);

  const firestoreAdapterRef = useRef(null);
  const handleGlobalMessageAddedRef = useRef(null);

  // Initialize Firestore adapter
  useEffect(() => {
    if (!firebaseToken || !user) {
      if (firestoreAdapterRef.current) {
        firestoreAdapterRef.current.destroy();
        firestoreAdapterRef.current = null;
        setConversations({});
        setConversationParticipants({});
      }
      return;
    }

    let isActive = true;
    const identity = `UID${user.id}~TID${user.team_id}`;
    const adapter = new FirestoreAdapter();
    setReady(false);

    adapter.on('conversationsUpdated', () => {
      if (!isActive) return;
      setConversations({ ...adapter.conversations });
    });
    adapter.on('participantsUpdated', () => {
      if (!isActive) return;
      setConversationParticipants({ ...adapter.participants });
    });
    adapter.on('messageAdded', (data) => {
      if (!isActive) return;
      handleGlobalMessageAddedRef.current?.(data);
    });

    adapter
      .init(firebaseToken, identity, {
        onRefreshToken: () => chat.getFirebaseToken(),
      })
      .then(() => {
        if (!isActive) return;
        firestoreAdapterRef.current = adapter;
        setConversations({ ...adapter.conversations });
        setConversationParticipants({ ...adapter.participants });
        setReady(true);
      })
      .catch((err) => {
        adapter.destroy();
        if (!isActive) return;
        Sentry.captureException(err);
        firestoreAdapterRef.current = null;
        setConversations({});
        setConversationParticipants({});
        setReady(true);
      });

    return () => {
      isActive = false;
      adapter.destroy();
      firestoreAdapterRef.current = null;
    };
  }, [firebaseToken, user?.id, user?.team_id]);

  // Global message notification handler
  const handleGlobalMessageAdded = useCallback(
    async (data) => {
      const { conversationId, message } = data;

      // Don't notify for open active conversation
      if (chatOpen && conversationId === activeConversationId) return;

      // Don't notify for own messages
      if (
        user &&
        message?.authorIdentity === `UID${user.id}~TID${user.team_id}`
      )
        return;

      // The notification payload has no conversation type or mute state, so
      // look it up from the adapter's conversation cache (mirrored here).
      const conv = conversations[conversationId];

      // Muted conversations stay silent: they still accumulate unread counts
      // (badge + bold row) but never raise a browser/SW notification.
      if (conv?.muted) return;

      // Browser notification (permission is requested in "Finish Setting up your profile" section)
      // Service worker (public/sw.js) is required for notifications on Chrome Android
      if (window.Notification && Notification.permission === 'granted') {
        // Marketplace listing conversations route to the marketplace inbox
        // for both buyers and sellers (one consistent destination) instead of
        // opening the chat popover via the /dashboard/chat/:id shortcut.
        const isMarketplace =
          (conv?.type || '').toUpperCase() === 'MARKETPLACE_LISTING';
        const targetUrl = isMarketplace
          ? `${PATH_DASHBOARD.marketplace.messages}?conversation=${encodeURIComponent(conversationId)}`
          : PATH_DASHBOARD.chatShortcut(conversationId);
        const title = conv?.friendlyName || 'New message';
        const options = {
          body: message?.text || '',
          icon: '/static/brand/logo_single.png',
          tag: message?.id,
          // `url` is what the service worker's notificationclick handler
          // navigates to; `conversationId` is kept for stale service workers
          // that still build the URL themselves.
          data: { conversationId, url: targetUrl },
        };

        const swReg = await navigator.serviceWorker
          ?.getRegistration()
          ?.catch(() => null);
        if (swReg) {
          swReg.showNotification(title, options);
        } else {
          const notification = new Notification(title, options);
          if (isMarketplace) {
            // Plain (non-service-worker) notifications have no default click
            // behavior. Marketplace chats must land on the marketplace inbox;
            // other types keep their existing (no-op) behavior.
            notification.onclick = () => {
              window.focus();
              window.location.assign(targetUrl);
            };
          }
        }
      }
    },
    [chatOpen, activeConversationId, user, conversations]
  );
  handleGlobalMessageAddedRef.current = handleGlobalMessageAdded;

  // Check if team and admin conversations exist (does not create them)
  useTeamConversation({ firebaseToken });
  useAdminTeamConversation({ firebaseToken, enabled: user?.is_admin });

  const sendMessage = useCallback(
    async (conversationId, text, { media } = {}) => {
      const adapter = firestoreAdapterRef.current;
      if (!adapter) return;

      // If sending to a pending chat (no conversation yet), create it first
      if (!conversationId && pendingChat) {
        let newConversationId;
        if (pendingChat.type === 'donation') {
          newConversationId = await chat.createDonationConversation(
            pendingChat.id,
            pendingChat.rescueId
          );
        } else if (pendingChat.type === 'team') {
          newConversationId = await chat.createTeamConversation();
        } else if (pendingChat.type === 'admin') {
          newConversationId = await chat.createAdminTeamConversation();
        }
        if (!newConversationId) return;
        setPendingChat(null);
        setActiveConversationId(newConversationId);
        await adapter.sendMessage(newConversationId, text, user?.name, media);
        return;
      }

      await adapter.sendMessage(conversationId, text, user?.name, media);
    },
    [user?.name, pendingChat]
  );

  const markRead = useCallback(async (conversationId) => {
    const adapter = firestoreAdapterRef.current;
    if (!adapter) return;
    await adapter.markRead(conversationId);
  }, []);

  const getMessages = useCallback(async (conversationId) => {
    const adapter = firestoreAdapterRef.current;
    if (!adapter) return { items: [], hasPrevPage: false };
    return adapter.getMessages(conversationId);
  }, []);

  const subscribeToMessages = useCallback((conversationId, callback) => {
    const adapter = firestoreAdapterRef.current;
    if (!adapter) return () => {};
    const handler = (data) => {
      if (data.conversationId === conversationId) {
        callback(data.message);
      }
    };
    adapter.on('messageAdded', handler);
    return () => adapter.off('messageAdded', handler);
  }, []);

  const toggleMute = useCallback(async (conversationId) => {
    const adapter = firestoreAdapterRef.current;
    if (!adapter) return;
    await adapter.toggleMute(conversationId);
  }, []);

  const unreadMessages = useMemo(
    () => getUnreadMessagesCount(conversations, user),
    [conversations, user]
  );

  const activeConversation = useMemo(
    () =>
      activeConversationId ? conversations[activeConversationId] || null : null,
    [activeConversationId, conversations]
  );

  // When an admin disables team/admin chat, the backend emits
  // `chat-settings-updated` to the team room. Re-fetch the user so the updated
  // team_chat_enabled / admin_chat_enabled flags hide the chat in this open
  // session. Re-join the room on (re)connect so this survives socket reconnects.
  const teamId = user?.team_id;
  const invalidateUserDataRef = useRef(invalidateUserData);
  invalidateUserDataRef.current = invalidateUserData;
  useEffect(() => {
    if (!teamId) return;
    const socket = SocketIO.getIO();
    if (!socket) return;

    const joinTeamRoom = () =>
      socket.emit('joinRoom', { type: 'team', id: teamId });

    const handleChatSettingsUpdated = (data) => {
      if (String(data?.teamId) !== String(teamId)) return;
      invalidateUserDataRef.current?.();
    };

    joinTeamRoom();
    socket.on('connect', joinTeamRoom);
    socket.on('chat-settings-updated', handleChatSettingsUpdated);

    return () => {
      socket.emit('leaveRoom', { type: 'team', id: teamId });
      socket.off('connect', joinTeamRoom);
      socket.off('chat-settings-updated', handleChatSettingsUpdated);
    };
  }, [teamId]);

  // Whenever the chat-enabled flags change (via the socket above
  // or a manual refresh), close the active/pending conversation if its type is
  // now disabled, so a stale open panel can't keep sending.
  const teamChatEnabled = user?.team_chat_enabled;
  const adminChatEnabled = user?.admin_chat_enabled;
  useEffect(() => {
    const isTypeDisabled = (type) =>
      (type === 'TEAM' && !teamChatEnabled) ||
      (type === 'ADMIN' && !adminChatEnabled);

    if (
      activeConversation &&
      isTypeDisabled(getConversationType(activeConversation, teamId))
    ) {
      setActiveConversationId(null);
    }

    if (pendingChat) {
      const pendingType = pendingChat.type?.toUpperCase();
      if (isTypeDisabled(pendingType)) {
        setPendingChat(null);
      }
    }
  }, [
    teamChatEnabled,
    adminChatEnabled,
    teamId,
    activeConversation,
    pendingChat,
  ]);

  const client = useMemo(() => {
    if (firestoreAdapterRef.current?.isReady())
      return firestoreAdapterRef.current;
    return null;
  }, [ready, conversations]);

  const fetchParticipantsOfAConversation = useCallback(
    async (conversationId) => {
      const adapter = firestoreAdapterRef.current;
      if (!adapter) return [];
      if (adapter.fetchParticipants) {
        return adapter.fetchParticipants(conversationId);
      }
      return [];
    },
    []
  );

  const chatLoading = !!firebaseToken && !ready;

  const value = useMemo(
    () => ({
      client,
      chatLoading,
      conversations,
      conversationParticipants,
      activeConversation,
      setActiveConversationId,
      pendingChat,
      setPendingChat,
      chatOpen,
      setChatOpen,
      drawerOpen,
      setDrawerOpen,
      unreadMessages,
      fetchParticipantsOfAConversation,
      sendMessage,
      markRead,
      getMessages,
      subscribeToMessages,
      toggleMute,
    }),
    [
      client,
      chatLoading,
      conversations,
      conversationParticipants,
      activeConversation,
      pendingChat,
      chatOpen,
      unreadMessages,
      fetchParticipantsOfAConversation,
      drawerOpen,
      setDrawerOpen,
      setChatOpen,
      sendMessage,
      markRead,
      getMessages,
      subscribeToMessages,
      toggleMute,
    ]
  );

  return (
    <ChatClientContext.Provider value={value}>
      {children}
    </ChatClientContext.Provider>
  );
}

export function useTeamConversation({ firebaseToken }) {
  const [state, setState] = useState({
    loading: false,
    valid: false,
    cache: null,
    error: null,
  });

  // Only businesses/npos can chat (for now)
  const { user } = useAuth();
  const canChat =
    user &&
    user.account_type === 'TEAM-STAFF' &&
    !(user.is_executive || user.is_corporate);

  useEffect(() => {
    if (state.valid || state.loading || !canChat) {
      return;
    }

    if (firebaseToken) {
      setState((state) => ({ ...state, loading: true }));
      chat
        .getTeamConversation()
        .then((conversationSid) =>
          setState({
            loading: false,
            valid: true,
            cache: conversationSid,
            error: null,
          })
        )
        .catch((error) => {
          Sentry.captureException(error);
          setState({
            loading: false,
            valid: true,
            cache: null,
            error,
          });
        });
    }
  }, [state, canChat, firebaseToken]);

  return state;
}

export function useAdminTeamConversation({ firebaseToken, enabled = true }) {
  const [state, setState] = useState({
    loading: false,
    valid: false,
    cache: null,
    error: null,
  });

  // Only businesses/npos can chat (for now)
  const { user } = useAuth();
  const canChat =
    user &&
    user.account_type === 'TEAM-STAFF' &&
    !(user.is_executive || user.is_corporate);

  useEffect(() => {
    if (state.valid || state.loading || !canChat || !enabled) {
      return;
    }

    if (firebaseToken) {
      setState((state) => ({ ...state, loading: true }));
      chat
        .getAdminTeamConversation()
        .then((conversationSid) =>
          setState({
            loading: false,
            valid: true,
            cache: conversationSid,
            error: null,
          })
        )
        .catch((error) => {
          Sentry.captureException(error);
          setState({
            loading: false,
            valid: true,
            cache: null,
            error,
          });
        });
    }
  }, [state, canChat, firebaseToken]);

  return state;
}

export default function useChatClient() {
  const context = useContext(ChatClientContext);
  if (!context) {
    throw new Error(
      'Could not read from the chat client context. Did you forget a <ChatClientProvider />?'
    );
  }

  return context;
}
